/**
 * PR-H — print preview viewport indicator.
 *
 * In *model space* the user normally cannot see what a paper-space
 * viewport will end up printing — they see the model entities, not
 * the framing rectangle of the viewport. The print-preview overlay
 * draws a translucent rectangle in model space that traces the camera
 * frustum of the active paper-space viewport, so the user can preview
 * "what'll print" without flipping to paper space.
 *
 * # When to show
 *
 *   - User is currently in *model space* (not paper).
 *   - At least one paper-space viewport is active.
 *
 * The host (Scene3DStage) decides on visibility and feeds the overlay
 * a `bounds` prop with the model-space rectangle to highlight.
 *
 * # Mesh strategy — HTML, not GL
 *
 * The overlay is a CSS-positioned `<div>` in the same HTML overlay
 * layer as the grips. The host projects the four model-space corners
 * into screen pixels each frame and feeds the overlay an AABB. We
 * accept a *projected* rect (CSS pixels) rather than world-space
 * bounds because:
 *
 *   - The shape is always axis-aligned in screen space (no rotated
 *     viewports in PR-H — those land alongside `XCLIP` polygon clips
 *     in PR-D viewport stencil pass).
 *   - HTML positioning is trivial in screen pixels; world→screen
 *     projection is one Vector3.project() call the host already does
 *     for grip projection.
 *
 * If the host needs to support rotated viewports later, this overlay
 * can be promoted to render an SVG `<polygon>` with the rotated
 * corners; for PR-H we keep it AABB.
 */

import * as React from "react";

export interface PrintPreviewBoundsProps {
  /**
   * Screen-space AABB to highlight (CSS pixels relative to the
   * overlay container). `null` = hide.
   */
  readonly bounds:
    | { left: number; top: number; right: number; bottom: number }
    | null;
  /** Optional label (defaults to viewport name). */
  readonly label?: string;
}

/**
 * Pure presentation. No R3F dependencies. The host owns world→screen
 * projection.
 */
export function PrintPreviewBounds(
  props: PrintPreviewBoundsProps,
): React.ReactElement | null {
  const { bounds, label } = props;
  if (!bounds) return null;
  const width = Math.max(bounds.right - bounds.left, 1);
  const height = Math.max(bounds.bottom - bounds.top, 1);
  return (
    <div
      data-testid="scene3d-print-preview-bounds"
      className="scene3d-print-preview-bounds"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        transform: `translate(${bounds.left}px, ${bounds.top}px)`,
        width,
        height,
        border: "1px solid rgba(241, 196, 15, 0.85)",
        background: "rgba(241, 196, 15, 0.08)",
        pointerEvents: "none",
        zIndex: 4,
        boxSizing: "border-box",
      }}
    >
      {label ? (
        <div
          style={{
            position: "absolute",
            left: 4,
            top: 4,
            fontSize: 10,
            fontFamily: "Inter, sans-serif",
            color: "rgba(241, 196, 15, 0.95)",
            background: "rgba(0, 0, 0, 0.45)",
            padding: "1px 4px",
            borderRadius: 2,
            pointerEvents: "none",
          }}
        >
          {label}
        </div>
      ) : null}
    </div>
  );
}
